import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Box,
  Alert,
  Snackbar,
  InputLabel,
  FormControl,
  Typography,
  Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import Load from '../Load';
import {
  LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID,
  LigaContaIuguDocumentosPorLigaIdData,
  LigaContaIuguDocumentosPorLigaIdVars,
  criarDocumentosComArquivos,
  atualizarDocumentosComArquivos,
  LigaContaIuguDocumentosData,
  LigaContaIuguDocumentosArquivos,
} from '../../services/ligaContaIuguService';
import type { LigaContaIuguDocumentos } from '../../types/ligaContaIugu';
import { TipoPessoa, TipoConta } from '../../types/ligaContaIugu';
import { dispatchGlobalError } from '../../context/ApiErrorContext';

interface LeagueDocumentacaoTabProps {
  ligaId: string;
  canEditLiga: boolean;
}

// Componente para exibir preview de arquivo salvo
interface FilePreviewProps {
  fileUrl: string;
  fileName: string;
  onRemove?: () => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({ fileUrl, fileName, onRemove }) => {
  const handleOpenFile = () => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  // Determina o tipo de arquivo pela extensão da URL
  const getFileType = (url: string): 'image' | 'pdf' | 'unknown' => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/)) {
      return 'image';
    }
    if (lowerUrl.match(/\.pdf(\?|$)/)) {
      return 'pdf';
    }
    return 'unknown';
  };

  const fileType = getFileType(fileUrl);

  return (
    <Box
      onClick={handleOpenFile}
      sx={{
        cursor: 'pointer',
        display: 'inline-block',
        position: 'relative',
        '&:hover': {
          opacity: 0.8,
        },
      }}
    >
      {fileType === 'image' ? (
        <Box
          component="img"
          src={fileUrl}
          alt={fileName}
          sx={{
            width: 120,
            height: 120,
            objectFit: 'cover',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      ) : fileType === 'pdf' ? (
        <Box
          sx={{
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'error.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'error.main',
            flexDirection: 'column',
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 48, color: 'error.main' }} />
          <Typography variant="caption" sx={{ mt: 1, color: 'error.main' }}>
            PDF
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300',
            flexDirection: 'column',
          }}
        >
          <ImageIcon sx={{ fontSize: 48, color: 'grey.500' }} />
          <Typography variant="caption" sx={{ mt: 1, color: 'grey.600' }}>
            Arquivo
          </Typography>
        </Box>
      )}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Clique para abrir
        </Typography>
        {onRemove && (
          <Typography
            variant="caption"
            sx={{ color: 'error.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            Remover
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

// Validation helpers
const validateCPF = (cpf: string | undefined): boolean => {
  if (!cpf) return true; // Optional field
  const cleaned = cpf.replace(/\D/g, '');
  return cleaned.length === 11;
};

const validateCNPJ = (cnpj: string | undefined): boolean => {
  if (!cnpj) return true; // Optional field
  const cleaned = cnpj.replace(/\D/g, '');
  return cleaned.length === 14;
};

const validatePhone = (phone: string | undefined): boolean => {
  if (!phone) return true; // Optional field
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 11;
};

// Form schema - all fields optional
const formSchema = z.object({
  tipoPessoa: z.nativeEnum(TipoPessoa),
  cpf: z.string().optional().refine(validateCPF, { message: 'CPF inválido (deve ter 11 dígitos)' }),
  nome: z.string().optional(),
  cnpj: z.string().optional().refine(validateCNPJ, { message: 'CNPJ inválido (deve ter 14 dígitos)' }),
  nomeEmpresa: z.string().optional(),
  nomeResp: z.string().optional(),
  respCpf: z.string().optional().refine(validateCPF, { message: 'CPF do responsável inválido' }),
  telefone: z.string().optional().refine(validatePhone, { message: 'Telefone inválido (10 ou 11 dígitos)' }),
  banco: z.string().optional(),
  bancoAg: z.string().optional(),
  tipoConta: z.nativeEnum(TipoConta).optional().nullable(),
  bancoCc: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const LeagueDocumentacaoTab: React.FC<LeagueDocumentacaoTabProps> = ({ ligaId, canEditLiga }) => {
  const apolloClient = useApolloClient();
  const { data, loading, error } = useQuery<
    LigaContaIuguDocumentosPorLigaIdData,
    LigaContaIuguDocumentosPorLigaIdVars
  >(LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID, {
    variables: { ligaId },
    skip: !ligaId,
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // File states
  const [arquivoIdFile, setArquivoIdFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [comprovanteEnderecoFile, setComprovanteEnderecoFile] = useState<File | null>(null);
  const [relatorioFinanceiroFile, setRelatorioFinanceiroFile] = useState<File | null>(null);
  const [contratoSocialFile, setContratoSocialFile] = useState<File | null>(null);

  // Preview URL states para arquivos selecionados
  const [arquivoIdPreview, setArquivoIdPreview] = useState<string | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [comprovanteEnderecoPreview, setComprovanteEnderecoPreview] = useState<string | null>(null);
  const [relatorioFinanceiroPreview, setRelatorioFinanceiroPreview] = useState<string | null>(null);
  const [contratoSocialPreview, setContratoSocialPreview] = useState<string | null>(null);

  // Removed states — controla se o usuário removeu explicitamente o arquivo salvo
  const [arquivoIdRemoved, setArquivoIdRemoved] = useState(false);
  const [selfieRemoved, setSelfieRemoved] = useState(false);
  const [comprovanteEnderecoRemoved, setComprovanteEnderecoRemoved] = useState(false);
  const [relatorioFinanceiroRemoved, setRelatorioFinanceiroRemoved] = useState(false);
  const [contratoSocialRemoved, setContratoSocialRemoved] = useState(false);

  // Refs para os inputs de arquivo (necessários para limpar o valor nativo ao remover)
  const arquivoIdInputRef = useRef<HTMLInputElement>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);
  const comprovanteEnderecoInputRef = useRef<HTMLInputElement>(null);
  const relatorioFinanceiroInputRef = useRef<HTMLInputElement>(null);
  const contratoSocialInputRef = useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tipoPessoa: TipoPessoa.PESSOA_FISICA,
      cpf: '',
      nome: '',
      cnpj: '',
      nomeEmpresa: '',
      nomeResp: '',
      respCpf: '',
      telefone: '',
      banco: '',
      bancoAg: '',
      tipoConta: null,
      bancoCc: '',
    },
  });

  const documentos = data?.ligaContaIuguDocumentosPorLigaId;

  useEffect(() => {
    if (documentos) {
      reset({
        tipoPessoa: documentos.tipoPessoa || TipoPessoa.PESSOA_FISICA,
        cpf: documentos.cpf || '',
        nome: documentos.nome || '',
        cnpj: documentos.cnpj || '',
        nomeEmpresa: documentos.nomeEmpresa || '',
        nomeResp: documentos.nomeResp || '',
        respCpf: documentos.respCpf || '',
        telefone: documentos.telefone || '',
        banco: documentos.banco || '',
        bancoAg: documentos.bancoAg || '',
        tipoConta: documentos.tipoConta || null,
        bancoCc: documentos.bancoCc || '',
      });
    }
  }, [documentos, reset]);

  // Limpar preview URLs ao desmontar o componente
  useEffect(() => {
    return () => {
      if (arquivoIdPreview) URL.revokeObjectURL(arquivoIdPreview);
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      if (comprovanteEnderecoPreview) URL.revokeObjectURL(comprovanteEnderecoPreview);
      if (relatorioFinanceiroPreview) URL.revokeObjectURL(relatorioFinanceiroPreview);
      if (contratoSocialPreview) URL.revokeObjectURL(contratoSocialPreview);
    };
  }, [arquivoIdPreview, selfiePreview, comprovanteEnderecoPreview, relatorioFinanceiroPreview, contratoSocialPreview]);

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // Mapeamento de nomes de campos para labels amigáveis
  const fieldLabels: Record<string, string> = {
    tipoPessoa: 'Tipo de Pessoa',
    cpf: 'CPF',
    nome: 'Nome',
    cnpj: 'CNPJ',
    nomeEmpresa: 'Nome da Empresa',
    nomeResp: 'Nome do Responsável',
    respCpf: 'CPF do Responsável',
    telefone: 'Telefone',
    banco: 'Banco',
    bancoAg: 'Agência',
    tipoConta: 'Tipo de Conta',
    bancoCc: 'Conta Corrente',
  };

  // File validation helper
  const validateFile = (file: File | null): boolean => {
    if (!file) return true; // Optional

    // Check file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      dispatchGlobalError('Apenas arquivos PDF, JPG, JPEG e PNG são permitidos.');
      return false;
    }

    // Check file size (15MB)
    if (file.size > 15 * 1024 * 1024) {
      dispatchGlobalError('O arquivo deve ter no máximo 15MB.');
      return false;
    }

    return true;
  };

  // File change handlers
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<File | null>>,
    previewSetter: React.Dispatch<React.SetStateAction<string | null>>,
    removedSetter: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    const file = e.target.files?.[0] || null;

    // Limpar preview anterior
    previewSetter((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    if (file && !validateFile(file)) {
      e.target.value = ''; // Clear input
      return;
    }

    setter(file);
    removedSetter(false);

    // Criar preview URL se houver arquivo
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      previewSetter(previewUrl);
    }
  };

  // Handler para remover arquivo selecionado ou salvo
  const handleFileRemove = (
    fileSetter: React.Dispatch<React.SetStateAction<File | null>>,
    previewSetter: React.Dispatch<React.SetStateAction<string | null>>,
    removedSetter: React.Dispatch<React.SetStateAction<boolean>>,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    fileSetter(null);
    previewSetter((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    removedSetter(true);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // Função chamada quando há erros de validação no formulário
  const onError = (errors: any) => {
    const errorFields = Object.keys(errors)
      .map((fieldName) => fieldLabels[fieldName] || fieldName)
      .join(', ');

    setSnackbar({
      open: true,
      message: `Por favor, corrija os seguintes campos: ${errorFields}`,
      severity: 'error',
    });
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const dataInput: LigaContaIuguDocumentosData = {
        ligaId,
        tipoPessoa: values.tipoPessoa,
        cpf: values.cpf && values.cpf.trim() !== '' ? values.cpf : null,
        nome: values.nome && values.nome.trim() !== '' ? values.nome : null,
        cnpj: values.cnpj && values.cnpj.trim() !== '' ? values.cnpj : null,
        nomeEmpresa: values.nomeEmpresa && values.nomeEmpresa.trim() !== '' ? values.nomeEmpresa : null,
        nomeResp: values.nomeResp && values.nomeResp.trim() !== '' ? values.nomeResp : null,
        respCpf: values.respCpf && values.respCpf.trim() !== '' ? values.respCpf : null,
        telefone: values.telefone && values.telefone.trim() !== '' ? values.telefone : null,
        banco: values.banco && values.banco.trim() !== '' ? values.banco : null,
        bancoAg: values.bancoAg && values.bancoAg.trim() !== '' ? values.bancoAg : null,
        tipoConta: values.tipoConta || null,
        bancoCc: values.bancoCc && values.bancoCc.trim() !== '' ? values.bancoCc : null,
        // Campos de URL: enviar '' quando o usuário removeu o arquivo existente
        arquivoIdUrl: arquivoIdRemoved && documentos?.arquivoIdUrl ? '' : undefined,
        selfieUrl: selfieRemoved && documentos?.selfieUrl ? '' : undefined,
        comprovanteEnderecoUrl: comprovanteEnderecoRemoved && documentos?.comprovanteEnderecoUrl ? '' : undefined,
        relatorioFinanceiroUrl: relatorioFinanceiroRemoved && documentos?.relatorioFinanceiroUrl ? '' : undefined,
        contratoSocialUrl: contratoSocialRemoved && documentos?.contratoSocialUrl ? '' : undefined,
        // Configurações de Negócio: enviados como null (não mais no formulário)
        priceRange: null,
        physicalProducts: null,
        businessType: null,
        automaticTransfer: null,
        estimatedRevenue: null,
      };

      // File = enviar arquivo, '' = remover arquivo existente, undefined = não alterado
      const arquivosInput: LigaContaIuguDocumentosArquivos = {
        arquivoId: arquivoIdFile || (arquivoIdRemoved && documentos?.arquivoIdUrl ? '' : undefined),
        selfie: selfieFile || (selfieRemoved && documentos?.selfieUrl ? '' : undefined),
        comprovanteEndereco: comprovanteEnderecoFile || (comprovanteEnderecoRemoved && documentos?.comprovanteEnderecoUrl ? '' : undefined),
        relatorioFinanceiro: relatorioFinanceiroFile || (relatorioFinanceiroRemoved && documentos?.relatorioFinanceiroUrl ? '' : undefined),
        contratoSocial: contratoSocialFile || (contratoSocialRemoved && documentos?.contratoSocialUrl ? '' : undefined),
      };

      let resultado: LigaContaIuguDocumentos;

      if (documentos?.id) {
        // Update existing record
        resultado = await atualizarDocumentosComArquivos(documentos.id, dataInput, arquivosInput);
        setSnackbar({
          open: true,
          message: 'Documentação atualizada com sucesso.',
          severity: 'success',
        });
      } else {
        // Create new record
        resultado = await criarDocumentosComArquivos(dataInput, arquivosInput);
        setSnackbar({
          open: true,
          message: 'Documentação criada com sucesso.',
          severity: 'success',
        });
      }

      // Atualizar o cache do Apollo com a resposta do REST
      apolloClient.writeQuery({
        query: LIGA_CONTA_IUGU_DOCUMENTOS_POR_LIGA_ID,
        variables: { ligaId },
        data: {
          ligaContaIuguDocumentosPorLigaId: {
            ...resultado,
            __typename: 'LigaContaIuguDocumentos',
          },
        },
      });

      // Clear file inputs after successful submission
      setArquivoIdFile(null);
      setSelfieFile(null);
      setComprovanteEnderecoFile(null);
      setRelatorioFinanceiroFile(null);
      setContratoSocialFile(null);
      setArquivoIdRemoved(false);
      setSelfieRemoved(false);
      setComprovanteEnderecoRemoved(false);
      setRelatorioFinanceiroRemoved(false);
      setContratoSocialRemoved(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: documentos?.id
          ? 'Erro ao atualizar documentação. Tente novamente.'
          : 'Erro ao criar documentação. Tente novamente.',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return <Load />;
  }

  if (error) {
    return <Alert severity="error">Erro ao carregar documentação. Tente novamente.</Alert>;
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader title="Documentação da Conta IUGU" />
        <CardContent>
          {!documentos && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Nenhuma documentação cadastrada. Preencha os campos abaixo e clique em Salvar para criar.
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <Stack spacing={4}>
              {/* Seção: Informações Pessoais/Empresa */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Informações Pessoais/Empresa
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Pessoa</InputLabel>
                    <Controller
                      name="tipoPessoa"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Tipo de Pessoa" disabled={!canEditLiga}>
                          <MenuItem value={TipoPessoa.PESSOA_FISICA}>Pessoa Física</MenuItem>
                          <MenuItem value={TipoPessoa.PESSOA_JURIDICA}>Pessoa Jurídica</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Controller
                      name="cpf"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CPF"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.cpf}
                          helperText={errors.cpf?.message as string}
                        />
                      )}
                    />
                    <Controller
                      name="nome"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.nome}
                          helperText={errors.nome?.message as string}
                        />
                      )}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Controller
                      name="cnpj"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CNPJ"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.cnpj}
                          helperText={errors.cnpj?.message as string}
                        />
                      )}
                    />
                    <Controller
                      name="nomeEmpresa"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome da Empresa"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.nomeEmpresa}
                          helperText={errors.nomeEmpresa?.message as string}
                        />
                      )}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Controller
                      name="nomeResp"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome do Responsável"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.nomeResp}
                          helperText={errors.nomeResp?.message as string}
                        />
                      )}
                    />
                    <Controller
                      name="respCpf"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CPF do Responsável"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.respCpf}
                          helperText={errors.respCpf?.message as string}
                        />
                      )}
                    />
                  </Stack>

                  <Controller
                    name="telefone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Telefone"
                        fullWidth
                        disabled={!canEditLiga}
                        error={!!errors.telefone}
                        helperText={errors.telefone?.message as string}
                      />
                    )}
                  />
                </Stack>
              </Box>

              {/* Seção: Dados Bancários */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Dados Bancários
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={3}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Controller
                      name="banco"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Banco"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.banco}
                          helperText={errors.banco?.message as string}
                        />
                      )}
                    />
                    <Controller
                      name="bancoAg"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Agência"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.bancoAg}
                          helperText={errors.bancoAg?.message as string}
                        />
                      )}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Conta</InputLabel>
                      <Controller
                        name="tipoConta"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} value={field.value || ''} label="Tipo de Conta" disabled={!canEditLiga}>
                            <MenuItem value="">Nenhum</MenuItem>
                            <MenuItem value={TipoConta.CORRENTE}>Conta Corrente</MenuItem>
                            <MenuItem value={TipoConta.POUPANCA}>Poupança</MenuItem>
                          </Select>
                        )}
                      />
                    </FormControl>
                    <Controller
                      name="bancoCc"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Conta Corrente"
                          fullWidth
                          disabled={!canEditLiga}
                          error={!!errors.bancoCc}
                          helperText={errors.bancoCc?.message as string}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </Box>

              {/* Seção: Documentos */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Documentos
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Arquivo de Identificação
                    </Typography>
                    <input
                      ref={arquivoIdInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, setArquivoIdFile, setArquivoIdPreview, setArquivoIdRemoved)}
                      disabled={!canEditLiga}
                      style={{ display: 'block', marginBottom: '8px' }}
                    />
                    {arquivoIdFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selecionado: {arquivoIdFile.name}
                      </Typography>
                    )}
                    {(arquivoIdPreview || (documentos?.arquivoIdUrl && !arquivoIdFile && !arquivoIdRemoved)) && (
                      <Box sx={{ mt: 1 }}>
                        <FilePreview
                          fileUrl={arquivoIdPreview || documentos?.arquivoIdUrl || ''}
                          fileName="Arquivo de Identificação"
                          onRemove={canEditLiga ? () => handleFileRemove(setArquivoIdFile, setArquivoIdPreview, setArquivoIdRemoved, arquivoIdInputRef) : undefined}
                        />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Formatos: PDF, JPG, JPEG, PNG. Máximo: 15MB
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Selfie
                    </Typography>
                    <input
                      ref={selfieInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, setSelfieFile, setSelfiePreview, setSelfieRemoved)}
                      disabled={!canEditLiga}
                      style={{ display: 'block', marginBottom: '8px' }}
                    />
                    {selfieFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selecionado: {selfieFile.name}
                      </Typography>
                    )}
                    {(selfiePreview || (documentos?.selfieUrl && !selfieFile && !selfieRemoved)) && (
                      <Box sx={{ mt: 1 }}>
                        <FilePreview
                          fileUrl={selfiePreview || documentos?.selfieUrl || ''}
                          fileName="Selfie"
                          onRemove={canEditLiga ? () => handleFileRemove(setSelfieFile, setSelfiePreview, setSelfieRemoved, selfieInputRef) : undefined}
                        />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Formatos: PDF, JPG, JPEG, PNG. Máximo: 15MB
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Comprovante de Endereço
                    </Typography>
                    <input
                      ref={comprovanteEnderecoInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, setComprovanteEnderecoFile, setComprovanteEnderecoPreview, setComprovanteEnderecoRemoved)}
                      disabled={!canEditLiga}
                      style={{ display: 'block', marginBottom: '8px' }}
                    />
                    {comprovanteEnderecoFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selecionado: {comprovanteEnderecoFile.name}
                      </Typography>
                    )}
                    {(comprovanteEnderecoPreview || (documentos?.comprovanteEnderecoUrl && !comprovanteEnderecoFile && !comprovanteEnderecoRemoved)) && (
                      <Box sx={{ mt: 1 }}>
                        <FilePreview
                          fileUrl={comprovanteEnderecoPreview || documentos?.comprovanteEnderecoUrl || ''}
                          fileName="Comprovante de Endereço"
                          onRemove={canEditLiga ? () => handleFileRemove(setComprovanteEnderecoFile, setComprovanteEnderecoPreview, setComprovanteEnderecoRemoved, comprovanteEnderecoInputRef) : undefined}
                        />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Formatos: PDF, JPG, JPEG, PNG. Máximo: 15MB
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Relatório Financeiro
                    </Typography>
                    <input
                      ref={relatorioFinanceiroInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, setRelatorioFinanceiroFile, setRelatorioFinanceiroPreview, setRelatorioFinanceiroRemoved)}
                      disabled={!canEditLiga}
                      style={{ display: 'block', marginBottom: '8px' }}
                    />
                    {relatorioFinanceiroFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selecionado: {relatorioFinanceiroFile.name}
                      </Typography>
                    )}
                    {(relatorioFinanceiroPreview || (documentos?.relatorioFinanceiroUrl && !relatorioFinanceiroFile && !relatorioFinanceiroRemoved)) && (
                      <Box sx={{ mt: 1 }}>
                        <FilePreview
                          fileUrl={relatorioFinanceiroPreview || documentos?.relatorioFinanceiroUrl || ''}
                          fileName="Relatório Financeiro"
                          onRemove={canEditLiga ? () => handleFileRemove(setRelatorioFinanceiroFile, setRelatorioFinanceiroPreview, setRelatorioFinanceiroRemoved, relatorioFinanceiroInputRef) : undefined}
                        />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Formatos: PDF, JPG, JPEG, PNG. Máximo: 15MB
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Contrato Social
                    </Typography>
                    <input
                      ref={contratoSocialInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, setContratoSocialFile, setContratoSocialPreview, setContratoSocialRemoved)}
                      disabled={!canEditLiga}
                      style={{ display: 'block', marginBottom: '8px' }}
                    />
                    {contratoSocialFile && (
                      <Typography variant="body2" color="text.secondary">
                        Selecionado: {contratoSocialFile.name}
                      </Typography>
                    )}
                    {(contratoSocialPreview || (documentos?.contratoSocialUrl && !contratoSocialFile && !contratoSocialRemoved)) && (
                      <Box sx={{ mt: 1 }}>
                        <FilePreview
                          fileUrl={contratoSocialPreview || documentos?.contratoSocialUrl || ''}
                          fileName="Contrato Social"
                          onRemove={canEditLiga ? () => handleFileRemove(setContratoSocialFile, setContratoSocialPreview, setContratoSocialRemoved, contratoSocialInputRef) : undefined}
                        />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Formatos: PDF, JPG, JPEG, PNG. Máximo: 15MB
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button type="submit" variant="contained" disabled={isSubmitting || !canEditLiga}>
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LeagueDocumentacaoTab;
